import instance from "../../axios";
import { getPeriodRanges } from "./periodRanges";

// Business 01 calls:
// Nx b1_mtd_revenue_split_cash — Total, Walk-in, Fleet, and the period trend (6 months, or full YTD / Last Year)
// 2x b1_mtd_revenue_split — comparison chart; fleet is invoiceSale + deliveryNotesSale
// 1x b1_mtd_revenue_history — historical average from fleet.sale / bfmc005.sale
// 2x b1_sales_performance_citywise — jobs, average ticket, and branch table
// bfmc005 is walk-in everywhere.

const asNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.replace(/,/g, "").replace(/%/g, "").trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const walkInOrFleet = (key) => {
  const name = String(key);
  if (/walk[\s_-]?in|garage|retail|b2c|^bfmc/i.test(name)) return "walkin";
  if (/fleet|maintenance|b2b/i.test(name)) return "fleet";
  return null;
};

const numberFromField = (node, name) => {
  if (!node || typeof node !== "object" || Array.isArray(node)) return null;
  const key = Object.keys(node).find((item) => item.toLowerCase() === name);
  return key ? asNumber(node[key]) : null;
};

const responseData = (payload) => {
  let value = payload;
  if (value && typeof value === "object" && !Array.isArray(value) && value.data && typeof value.data === "object") {
    value = value.data;
  }
  return value;
};

const addInvoiceSaleAndDeliveryNoteSale = (value) => {
  const invoiceSale = numberFromField(value, "invoicesale");
  const deliveryNotesSale = numberFromField(value, "deliverynotespurchase");
  if (invoiceSale == null && deliveryNotesSale == null) return null;
  return (deliveryNotesSale || 0);
};

const sumInvoiceSaleAndDeliveryNoteSale = (node) => {
  if (!node || typeof node !== "object") return null;
  if (!Array.isArray(node)) {
    const direct = addInvoiceSaleAndDeliveryNoteSale(node);
    if (direct != null) return direct;
  }
  const children = Array.isArray(node) ? node : Object.keys(node).map((key) => node[key]);
  let total = null;
  children.forEach((child) => {
    if (!child || typeof child !== "object") return;
    const part = addInvoiceSaleAndDeliveryNoteSale(child);
    if (part == null) return;
    total = (total || 0) + part;
  });
  return total;
};

const revenueForWalkInOrFleet = (node) => {
  const summed = sumInvoiceSaleAndDeliveryNoteSale(node);
  if (summed != null) return summed;
  // return numberFromField(node, "sale");
};

const walkInFleetAndTotal = (node) => {
  const blank = { walkin: null, fleet: null, total: null };
  if (!node || typeof node !== "object" || Array.isArray(node)) return blank;
  let walkin = null;
  let fleet = null;
  Object.keys(node).forEach((key) => {
    const channel = walkInOrFleet(key);
    if (channel !== "walkin" && channel !== "fleet") return;
    const amount = revenueForWalkInOrFleet(node[key]);
    if (amount == null) return;
    if (channel === "walkin") walkin = (walkin || 0) + amount;
    else fleet = (fleet || 0) + amount;
  });
  if (walkin == null && fleet == null) return blank;
  return { walkin, fleet, total: (walkin || 0) + (fleet || 0) };
};

const readWalkInFleetAndTotal = (payload) => walkInFleetAndTotal(responseData(payload));

const fleetInvoiceSalePlusDeliveryNoteSale = (payload) => {
  const value = responseData(payload);
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const key = Object.keys(value).find((item) => walkInOrFleet(item) === "fleet");
  if (!key) return null;
  return sumInvoiceSaleAndDeliveryNoteSale(value[key]);
};

const setFleetToInvoicePlusDeliveryNote = (amounts, payload) => {
  const fleet = fleetInvoiceSalePlusDeliveryNoteSale(payload);
  if (fleet == null) return amounts;
  const walkin = amounts?.walkin ?? null;
  return {
    walkin,
    fleet,
    total: walkin != null ? walkin + fleet : fleet,
  };
};

const MONTH_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const calendarMonthIndex = (label) => MONTH_INDEX[String(label || "").trim().toLowerCase().slice(0, 3)];

const responseIsGroupedByMonth = (obj) => {
  const keys = obj && typeof obj === "object" && !Array.isArray(obj) ? Object.keys(obj) : [];
  if (!keys.length) return false;
  return keys.every((key) => calendarMonthIndex(key) != null);
};

const hasWalkInOrFleetAmount = (amounts) =>
  amounts?.walkin != null || amounts?.fleet != null || amounts?.total != null;

const averageWalkInFleetAndTotal = (rows) => {
  const avg = (key) => {
    const nums = rows.map((row) => row[key]).filter((n) => n != null);
    if (!nums.length) return null;
    return nums.reduce((sum, n) => sum + n, 0) / nums.length;
  };
  const walkin = avg("walkin");
  const fleet = avg("fleet");
  const total = walkin != null || fleet != null ? (walkin || 0) + (fleet || 0) : avg("total");
  return { walkin, fleet, total };
};

const walkInFleetAndTotalFromSale = (node) => {
  const blank = { walkin: null, fleet: null, total: null };
  if (!node || typeof node !== "object" || Array.isArray(node)) return blank;
  let walkin = null;
  let fleet = null;
  Object.keys(node).forEach((key) => {
    const channel = walkInOrFleet(key);
    if (channel !== "walkin" && channel !== "fleet") return;
    const amount = numberFromField(node[key], "purchase");
    if (amount == null) return;
    if (channel === "walkin") walkin = (walkin || 0) + amount;
    else fleet = (fleet || 0) + amount;
  });
  if (walkin == null && fleet == null) return blank;
  return { walkin, fleet, total: (walkin || 0) + (fleet || 0) };
};

const readHistoryMonths = (payload) => {
  const value = responseData(payload);
  if (!responseIsGroupedByMonth(value)) return [];
  return Object.keys(value)
    .map((key) => ({ label: key, order: calendarMonthIndex(key), ...walkInFleetAndTotalFromSale(value[key]) }))
    .filter((row) => row.order != null && hasWalkInOrFleetAmount(row))
    .sort((a, b) => a.order - b.order);
};

const recordHasCityNumbers = (node) =>
  numberFromField(node, "deliverynotescount") != null ||
  numberFromField(node, "averageticket") != null ||
  numberFromField(node, "purchasenetamount") != null;

const visitEachCity = (payload, visit) => {
  const value = responseData(payload);
  if (!value || typeof value !== "object" || Array.isArray(value)) return;
  Object.keys(value).forEach((key) => {
    const channel = walkInOrFleet(key);
    if (channel !== "walkin" && channel !== "fleet") return;
    const channelData = value[key];
    if (!channelData || typeof channelData !== "object" || Array.isArray(channelData)) return;
    if (recordHasCityNumbers(channelData)) {
      visit(channel, key, channelData);
      return;
    }
    Object.keys(channelData).forEach((city) => {
      const stats = channelData[city];
      if (stats && typeof stats === "object") visit(channel, city, stats);
    });
  });
};

const sumDeliveryNoteCounts = (payload) => {
  const totals = { walkin: 0, fleet: 0, total: null };
  let seen = false;
  visitEachCity(payload, (channel, _city, stats) => {
    const count = numberFromField(stats, "deliverynotescount");
    if (count == null) return;
    seen = true;
    totals[channel] += count;
  });
  if (!seen) return { walkin: null, fleet: null, total: null };
  totals.total = totals.walkin + totals.fleet;
  return totals;
};

const averageOfValuesAboveZero = (values) => {
  const nums = values.filter((value) => value != null && value > 0);
  if (!nums.length) return null;
  return nums.reduce((sum, value) => sum + value, 0) / nums.length;
};

const averageTicketAboveZero = (payload) => {
  const lists = { walkin: [], fleet: [], all: [] };
  visitEachCity(payload, (channel, _city, stats) => {
    const ticket = numberFromField(stats, "averageticket");
    if (ticket == null) return;
    lists[channel].push(ticket);
    lists.all.push(ticket);
  });
  return {
    walkin: averageOfValuesAboveZero(lists.walkin),
    fleet: averageOfValuesAboveZero(lists.fleet),
    total: averageOfValuesAboveZero(lists.all),
  };
};

const percentVersusPrevious = (current, previous) => {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

const cityPerformanceRows = (currentPayload, previousPayload) => {
  const rows = new Map();
  const ensure = (name) => {
    const key = String(name).trim().toLowerCase();
    if (!rows.has(key)) {
      rows.set(key, {
        name,
        walkin: 0,
        fleet: 0,
        walkinProfit: 0,
        fleetProfit: 0,
        walkinTicket: null,
        fleetTicket: null,
      });
    }
    return rows.get(key);
  };

  visitEachCity(currentPayload, (channel, city, stats) => {
    const row = ensure(city);
    const sale = numberFromField(stats, "purchasenetamount") || 0;
    const profit = numberFromField(stats, "profit") || 0;
    const ticket = numberFromField(stats, "averageticket");
    if (channel === "walkin") {
      row.walkin += sale;
      row.walkinProfit += profit;
      if (ticket > 0) row.walkinTicket = ticket;
    } else {
      row.fleet += sale;
      row.fleetProfit += profit;
      if (ticket > 0) row.fleetTicket = ticket;
    }
  });

  const previousTotals = new Map();
  visitEachCity(previousPayload, (_channel, city, stats) => {
    const key = String(city).trim().toLowerCase();
    previousTotals.set(key, (previousTotals.get(key) || 0) + (numberFromField(stats, "purchasenetamount") || 0));
  });

  return [...rows.values()]
    .map((row) => {
      const total = row.walkin + row.fleet;
      const profit = row.walkinProfit + row.fleetProfit;
      console.log("Dataaaa",total, previousTotals)
      return {
        name: row.name,
        total,
        walkin: row.walkin,
        fleet: row.fleet,
        growth: percentVersusPrevious(total, previousTotals.get(String(row.name).trim().toLowerCase())),
        walkinTicket: row.walkinTicket,
        fleetTicket: row.fleetTicket,
        margin: total > 0 ? (profit / total) * 100 : null,
      };
    })
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total);
};

const saleNetAmountByCity = (payload) => {
  const byCity = new Map();
  visitEachCity(payload, (_channel, city, stats) => {
    const sale = numberFromField(stats, "purchasenetamount") || 0;
    const key = String(city).trim().toLowerCase();
    const existing = byCity.get(key) || { name: String(city).trim(), sale: 0 };
    existing.sale += sale;
    byCity.set(key, existing);
  });
  return byCity;
};

const citySaleShares = (currentPayload, previousPayload) => {
  const current = saleNetAmountByCity(currentPayload);
  const previous = saleNetAmountByCity(previousPayload);
  const currentTotal = [...current.values()].reduce((sum, row) => sum + row.sale, 0);
  const previousTotal = [...previous.values()].reduce((sum, row) => sum + row.sale, 0);
  return [...current.values()]
    .filter((row) => row.sale > 0)
    .map((row) => {
      const key = row.name.trim().toLowerCase();
      const share = currentTotal > 0 ? (row.sale / currentTotal) * 100 : 0;
      const previousSale = previous.get(key)?.sale || 0;
      const previousShare = previousTotal > 0 ? (previousSale / previousTotal) * 100 : null;
      return {
        name: row.name,
        share,
        change: previousShare == null ? null : share - previousShare,
      };
    })
    .sort((a, b) => b.share - a.share);
};

/** Station codes used when "OBD Workshop" is enabled on Workshop Revenue & Operations APIs. */
export const WORKSHOP_STATION_CODES = [1, 185];

const stationCodeQuery = (stationCodes) => {
  if (!Array.isArray(stationCodes) || !stationCodes.length) return "";
  return stationCodes.map((code) => `sc=${encodeURIComponent(code)}`).join("&");
};

const appendQuery = (url, extraQuery) => {
  if (!extraQuery) return url;
  return url.includes("?") ? `${url}&${extraQuery}` : `${url}?${extraQuery}`;
};

const urlWithFromAndTo = (path, range, stationCodes) => {
  const base = `${path}?from=${range.from}&to=${range.to}`;
  return appendQuery(base, stationCodeQuery(stationCodes));
};

const urlWithStationCodes = (path, stationCodes) =>
  appendQuery(path, stationCodeQuery(stationCodes));

const fetchDashboard = (url) =>
  instance({ url, method: "GET" }).catch((error) => {
    console.error("Business dashboard request failed", url, error?.message || error);
    return null;
  });

const buildBusinessOne = (trendRanges, currentRange, previousRange, raw) => {
  const historyRows = readHistoryMonths(raw.history);
  const historyByOrder = new Map(historyRows.map((row) => [row.order, row]));
  const cashTrend = raw.cashTrend.map((payload, index) => ({
    label: trendRanges[index].label,
    ...readWalkInFleetAndTotal(payload),
  }));
  const months = cashTrend.map((cash, index) => {
    const fromHistory = historyByOrder.get(calendarMonthIndex(trendRanges[index].label));
    const isRecent = index >= cashTrend.length - 2;
    if (fromHistory && (!isRecent || !hasWalkInOrFleetAmount(cash))) {
      return {
        label: trendRanges[index].label,
        walkin: fromHistory.walkin,
        fleet: fromHistory.fleet,
        total: fromHistory.total,
      };
    }
    return { label: cash.label, walkin: cash.walkin, fleet: cash.fleet, total: cash.total };
  });
  const currentCash = readWalkInFleetAndTotal(raw.cashCur);
  const previousCash = readWalkInFleetAndTotal(raw.cashPrev);
  const current = hasWalkInOrFleetAmount(currentCash)
    ? currentCash
    : cashTrend[cashTrend.length - 1] || { walkin: null, fleet: null, total: null };
  const previous = hasWalkInOrFleetAmount(previousCash)
    ? previousCash
    : cashTrend[cashTrend.length - 2] || { walkin: null, fleet: null, total: null };
  const splitCurrent = readWalkInFleetAndTotal(raw.splitCur);
  const splitPrevious = readWalkInFleetAndTotal(raw.splitPrev);

  return {
    months,
    current: { walkin: current.walkin, fleet: current.fleet, total: current.total },
    previous: { walkin: previous.walkin, fleet: previous.fleet, total: previous.total },
    historyAverage: averageWalkInFleetAndTotal(historyRows),
    comparisonCurrent: setFleetToInvoicePlusDeliveryNote(
      setFleetToInvoicePlusDeliveryNote(
        hasWalkInOrFleetAmount(splitCurrent) ? splitCurrent : {
          walkin: current.walkin,
          fleet: current.fleet,
          total: current.total,
        },
        raw.splitCur
      ),
      raw.cashCur
    ),
    comparisonPrevious: setFleetToInvoicePlusDeliveryNote(
      setFleetToInvoicePlusDeliveryNote(
        hasWalkInOrFleetAmount(splitPrevious) ? splitPrevious : {
          walkin: previous.walkin,
          fleet: previous.fleet,
          total: previous.total,
        },
        raw.splitPrev
      ),
      raw.cashPrev
    ),
    jobs: sumDeliveryNoteCounts(raw.cityCur),
    jobsPrev: sumDeliveryNoteCounts(raw.cityPrev),
    tickets: averageTicketAboveZero(raw.cityCur),
    ticketsPrev: averageTicketAboveZero(raw.cityPrev),
    branches: cityPerformanceRows(raw.cityCur, raw.cityPrev),
    regions: citySaleShares(raw.cityCur, raw.cityPrev),
    range: { current: currentRange, previous: previousRange },
  };
};

export const loadWorkshopBusiness = async (timezone, period = "MTD", options = {}) => {
  const stationCodes = options.filterByStationCode ? WORKSHOP_STATION_CODES : null;
  const { current, previous, trend } = getPeriodRanges(period, timezone);
  const trendCount = trend.length;
  const results = await Promise.all([
    ...trend.map((range) =>
      fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_mtd_revenue_split_cash", range, stationCodes))
    ),
    fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_mtd_revenue_split_cash", current, stationCodes)),
    fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_mtd_revenue_split_cash", previous, stationCodes)),
    fetchDashboard(urlWithStationCodes("/api/businessDashboard/b1_mtd_revenue_history", stationCodes)),
    fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_mtd_revenue_split", current, stationCodes)),
    fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_mtd_revenue_split", previous, stationCodes)),
    fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_sales_performance_citywise", current, stationCodes)),
    fetchDashboard(urlWithFromAndTo("/api/businessDashboard/b1_sales_performance_citywise", previous, stationCodes)),
  ]);

  return buildBusinessOne(trend, current, previous, {
    cashTrend: results.slice(0, trendCount),
    cashCur: results[trendCount],
    cashPrev: results[trendCount + 1],
    history: results[trendCount + 2],
    splitCur: results[trendCount + 3],
    splitPrev: results[trendCount + 4],
    cityCur: results[trendCount + 5],
    cityPrev: results[trendCount + 6],
  });
};

export const formatRevenue = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1000000) {
    const millions = abs / 1000000;
    const text = millions >= 100 ? millions.toFixed(0) : millions.toFixed(1);
    return `${sign}${text.replace(/\.0$/, "")}M`;
  }
  if (abs >= 1000) {
    const thousands = abs / 1000;
    const text = thousands >= 100 ? thousands.toFixed(0) : thousands.toFixed(1);
    return `${sign}${text.replace(/\.0$/, "")}K`;
  }
  return `${sign}${Math.round(abs).toLocaleString()}`;
};

export const formatCount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Math.round(Number(value)).toLocaleString();
};

export const formatSignedPct = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

export const vsPrev = (current, previous, withLabel) => {
  const pct = percentVersusPrevious(current, previous);
  if (pct == null) return null;
  const arrow = pct > 0 ? "▲" : pct < 0 ? "▼" : "▲";
  const suffix = withLabel ? " vs Prev" : "";
  return {
    text: `${arrow} ${Math.abs(pct).toFixed(1)}%${suffix}`,
    tone: pct > 0 ? "bd-up" : pct < 0 ? "bd-down" : "bd-muted",
  };
};

export const vsAvg = (current, average) => {
  const pct = percentVersusPrevious(current, average);
  if (pct == null) return null;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}% vs Avg`;
};

export const shareOf = (part, total) => {
  if (part == null || !total) return "—";
  return `${((part / total) * 100).toFixed(1)}% of total jobs`;
};
